import { useQuery } from "@tanstack/react-query";
import { projectQuery } from "@/lib/projectsQuery";
import { US_REGIONS } from "@shared/address";
import { setDisplayCurrency, setDisplayTimeZone } from "@/lib/format";
import { setFileOwner } from "@/lib/download";
import { projectTimeZone } from "@shared/timeZones";

/**
 * The country a project is in, read from its province or state ("FL" → "US").
 * An address with no country of its own is offered this country's provinces
 * or states and postal-code shape, so a Florida project's buyer gets ZIP
 * codes without picking a country first. "CA" until the project has loaded.
 */
export function useProjectCountry(projectId: string | null | undefined): "CA" | "US" {
  const { data } = useQuery(projectQuery(projectId ?? undefined));
  const region = String(data?.province ?? "").toUpperCase();
  return US_REGIONS.some((r) => r.code === region) ? "US" : "CA";
}

/** The project's own province or state code ("ON", "FL"), or null until it loads. */
export function useProjectRegion(projectId: string | null | undefined): string | null {
  const { data } = useQuery(projectQuery(projectId ?? undefined));
  return data?.province ? String(data.province) : null;
}

/**
 * Keeps the formatters' currency on the open project's (lib/format.ts):
 * USD on a Florida or Texas project, CAD otherwise. Set during render, before
 * the page below renders its prices; returns the currency so the shell can
 * key its content on it and re-render every figure if the project's arrives
 * after a first paint in the default.
 */
export function useProjectCurrencySync(projectId: string | null | undefined): "CAD" | "USD" {
  const currency = useProjectCountry(projectId) === "US" ? "USD" : "CAD";
  setDisplayCurrency(currency);
  return currency;
}

/**
 * Keeps the formatters' display zone on the open project's (lib/format.ts):
 * America/Chicago on a Texas project, Toronto on an Ontario one and outside
 * any project. Set during render like the currency, and returned so the shell
 * can key its content on it. The project's name goes with it: it is the
 * owner every file the project hands out is named for (lib/download.ts).
 */
export function useProjectTimeZoneSync(projectId: string | null | undefined): string {
  const { data } = useQuery(projectQuery(projectId ?? undefined));
  const timeZone = projectTimeZone(data?.province ? String(data.province) : null);
  setDisplayTimeZone(timeZone);
  setFileOwner(projectId && typeof data?.projectName === "string" ? data.projectName : null);
  return timeZone;
}
